import { useAgentSSE } from '@/hooks/useAgentSSE';
import { AgentRunAttachment, InterruptData } from '@superfunnel/types';
import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ToolCallEmitter, ToolCallHandler } from '../services/toolCallEmitter';
import {
  AgentChatState,
  AgentContext,
  AgentContextMap,
  ChatContextValue,
  ChatMessage,
  InterruptResponse,
  PromptAttachment,
  SSEGenericEvent,
  StreamEvent,
  ToolCall,
} from '../types/agentChat';
import { useAnalytics } from './AnalyticsContext';

// Action types for the reducer
type ChatAction =
  | { type: 'ADD_MESSAGE'; message: ChatMessage }
  | { type: 'UPDATE_STREAMING_MESSAGE'; content: string }
  | { type: 'START_MESSAGE'; reason?: string }
  | { type: 'END_MESSAGE'; reason?: string }
  | {
      type: 'SET_WORKING';

      working: boolean;
      status?: string;
    }
  | { type: 'ADD_TOOL_CALL'; toolCall: ToolCall }
  | {
      type: 'COMPLETE_TOOL_CALL';
      toolCallId?: string;
      toolName: string;
      input?: unknown;
      output?: unknown;
    }
  | { type: 'FINISH_STREAMING'; events: SSEGenericEvent[] }
  | { type: 'SET_LOADING'; loading: boolean }
  | {
      type: 'SET_STREAMING';
      streaming: boolean;
      runId?: string;
      threadId?: string;
    }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'REMOVE_LAST_ERROR' }
  | { type: 'STOP_ALL_TOOL_CALLS' }
  | { type: 'REMOVE_RUNNING_TOOL_CALLS' }
  | { type: 'CLEAR_CONVERSATION' }
  | { type: 'UPDATE_LANDING_PAGE_JSON'; json: any }
  | { type: 'SET_AGENT_CONTEXT'; context: AgentContext }
  | { type: 'SET_THREAD_ID'; threadId: string }
  | { type: 'SET_INTERRUPTED'; interruptData: InterruptData }
  | { type: 'CLEAR_INTERRUPT' };

const initialAgentState: AgentChatState = {
  agentName: 'landingPageAgent',
  messages: [],
  isLoading: false,
  isStreaming: false,
  isWorking: false,
  isInterrupted: false,
  interruptData: undefined,
  error: null,
  currentRunId: null,
  currentThreadId: null,
  landingPageJson: null,
  lastUpdated: null,
};

function chatReducer(
  agentState: AgentChatState,
  action: ChatAction,
): AgentChatState {
  switch (action.type) {
    case 'ADD_MESSAGE': {
      return {
        ...agentState,
        messages: [...agentState.messages, action.message],
        lastUpdated: new Date(),
      };
    }

    case 'START_MESSAGE': {
      // Create a new streaming message for the agent
      const agentMessageId = uuidv4();
      const agentMessage: ChatMessage = {
        id: agentMessageId,
        type: 'agent',
        content: '',
        timestamp: new Date(),
        agentName: agentState.agentName,
        isStreaming: true,
        metadata: {
          runId: agentState.currentRunId || undefined,
          messageStartReason: action.reason,
        },
      };

      return {
        ...agentState,
        messages: [...agentState.messages, agentMessage],
        lastUpdated: new Date(),
      };
    }

    case 'END_MESSAGE': {
      const messages = [...agentState.messages];
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage.isStreaming) {
        messages[messages.length - 1] = {
          ...lastMessage,
          isStreaming: false,
          metadata: {
            ...lastMessage.metadata,
            messageEndReason: action.reason,
          },
        };
      }

      return {
        ...agentState,
        messages,
        lastUpdated: new Date(),
      };
    }

    case 'SET_WORKING': {
      return {
        ...agentState,
        isWorking: action.working,
        workingStatus: action.status,
        lastUpdated: new Date(),
      };
    }

    case 'ADD_TOOL_CALL': {
      const messages = [...agentState.messages];
      let lastMessage = messages[messages.length - 1];

      // If there's no message OR last message is not an agent message OR last message is complete (not streaming)
      // Create a new agent message to hold this tool call
      if (
        !lastMessage ||
        lastMessage.type !== 'agent' ||
        lastMessage.content.trim()
      ) {
        const newAgentMessage: ChatMessage = {
          id: uuidv4(),
          type: 'agent',
          content: '',
          timestamp: new Date(),
          agentName: agentState.agentName,
          isStreaming: false,
          toolCalls: [action.toolCall],
        };
        messages.push(newAgentMessage);
      } else {
        // Add tool call to existing streaming message
        lastMessage = {
          ...lastMessage,
          toolCalls: [...(lastMessage.toolCalls || []), action.toolCall],
        };
        messages[messages.length - 1] = lastMessage;
      }

      return {
        ...agentState,
        messages,
        lastUpdated: new Date(),
      };
    }

    case 'COMPLETE_TOOL_CALL': {
      const messages = [...agentState.messages];

      const matchedMessage = action.toolCallId
        ? messages.find((msg) =>
            msg.toolCalls?.some(
              (tc) =>
                tc.toolCallId === action.toolCallId && tc.status === 'running',
            ),
          )
        : undefined;

      if (!matchedMessage) return agentState;

      const toolCalls = matchedMessage.toolCalls;
      if (!toolCalls) return agentState;

      // Find from end (most recent)
      for (let i = toolCalls.length - 1; i >= 0; i--) {
        // First try to match by toolCallId if available (more precise)
        const matchById =
          action.toolCallId &&
          toolCalls[i].toolCallId === action.toolCallId &&
          toolCalls[i].status === 'running';

        if (matchById) {
          const endTime = new Date();
          toolCalls[i] = {
            ...toolCalls[i],
            status: 'completed',
            endTime,
            duration: endTime.getTime() - toolCalls[i].startTime.getTime(),
            // Update input if provided (tool_end has both input and output)
            ...(action.input !== undefined && { input: action.input }),
            output: action.output,
          };
          break; // Only complete the first match (most recent)
        }
      }

      return {
        ...agentState,
        messages,
        lastUpdated: new Date(),
      };
    }

    case 'UPDATE_STREAMING_MESSAGE': {
      const messages = [...agentState.messages];
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage.isStreaming) {
        messages[messages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + action.content,
        };
      }

      return {
        ...agentState,
        messages,
        lastUpdated: new Date(),
      };
    }

    case 'FINISH_STREAMING': {
      const messages = [...agentState.messages];
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage.isStreaming) {
        messages[messages.length - 1] = {
          ...lastMessage,
          isStreaming: false,
          metadata: {
            ...lastMessage.metadata,
            rawEvents: action.events,
          },
        };
      }

      // Extract landing page JSON from events
      let landingPageJson = agentState.landingPageJson;
      for (const event of action.events) {
        if (event.name === 'landingPageUpdate' || event.name === 'pageUpdate') {
          landingPageJson = event.data;
        }
      }

      return {
        ...agentState,
        messages,
        isStreaming: false,
        isWorking: false,
        currentRunId: null,
        landingPageJson,
        lastUpdated: new Date(),
      };
    }

    case 'SET_LOADING':
      return {
        ...agentState,
        isLoading: action.loading,
      };

    case 'SET_STREAMING':
      return {
        ...agentState,
        isStreaming: action.streaming,
        ...(action.runId !== undefined && { currentRunId: action.runId }),
        ...(action.threadId !== undefined && {
          currentThreadId: action.threadId,
        }),
      };

    case 'SET_ERROR':
      return {
        ...agentState,
        error: action.error,
        isLoading: false,
        isStreaming: false,
      };

    case 'REMOVE_LAST_ERROR': {
      const messages = [...agentState.messages];
      // Find and remove the last error message
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].type === 'error') {
          messages.splice(i, 1);
          break;
        }
      }
      return {
        ...agentState,
        messages,
      };
    }

    case 'STOP_ALL_TOOL_CALLS': {
      const messages = [...agentState.messages];
      let hasChanges = false;

      // Find all messages with running tool calls and mark them as stopped
      messages.forEach((message, messageIndex) => {
        if (message.toolCalls && message.toolCalls.length > 0) {
          const updatedToolCalls = message.toolCalls.map((toolCall) => {
            if (toolCall.status === 'running') {
              hasChanges = true;
              const endTime = new Date();
              return {
                ...toolCall,
                status: 'stopped' as const,
                endTime,
                duration: endTime.getTime() - toolCall.startTime.getTime(),
              };
            }
            return toolCall;
          });

          if (hasChanges) {
            messages[messageIndex] = {
              ...message,
              toolCalls: updatedToolCalls,
            };
          }
        }
      });

      return hasChanges
        ? {
            ...agentState,
            messages,
          }
        : agentState;
    }

    case 'REMOVE_RUNNING_TOOL_CALLS': {
      // Remove running tool calls (used when interrupt happens - they'll be re-added when agent resumes)
      const messages = [...agentState.messages];
      let hasChanges = false;

      messages.forEach((message, messageIndex) => {
        if (message.toolCalls && message.toolCalls.length > 0) {
          const filteredToolCalls = message.toolCalls.filter(
            (toolCall) => toolCall.status !== 'running',
          );

          if (filteredToolCalls.length !== message.toolCalls.length) {
            hasChanges = true;
            messages[messageIndex] = {
              ...message,
              toolCalls:
                filteredToolCalls.length > 0 ? filteredToolCalls : undefined,
            };
          }
        }
      });

      return hasChanges
        ? {
            ...agentState,
            messages,
          }
        : agentState;
    }

    case 'CLEAR_CONVERSATION':
      return {
        ...initialAgentState,
        context: agentState.context,
        agentName: agentState.agentName,
      };

    case 'UPDATE_LANDING_PAGE_JSON':
      return {
        ...agentState,
        landingPageJson: action.json,
        lastUpdated: new Date(),
      };

    case 'SET_AGENT_CONTEXT':
      return {
        ...agentState,
        context: action.context,
        lastUpdated: new Date(),
      };

    case 'SET_THREAD_ID':
      return {
        ...agentState,
        currentThreadId: action.threadId,
        lastUpdated: new Date(),
      };

    case 'SET_INTERRUPTED': {
      // When interrupted, remove running tool calls (they'll be shown fresh when agent resumes)
      const messages = [...agentState.messages];

      messages.forEach((message, messageIndex) => {
        if (message.toolCalls && message.toolCalls.length > 0) {
          const filteredToolCalls = message.toolCalls.filter(
            (toolCall) => toolCall.status !== 'running',
          );

          if (filteredToolCalls.length !== message.toolCalls.length) {
            messages[messageIndex] = {
              ...message,
              toolCalls:
                filteredToolCalls.length > 0 ? filteredToolCalls : undefined,
            };
          }
        }
      });

      return {
        ...agentState,
        messages,
        isInterrupted: true,
        isStreaming: false,
        isWorking: false,
        interruptData: action.interruptData,
        lastUpdated: new Date(),
      };
    }

    case 'CLEAR_INTERRUPT':
      return {
        ...agentState,
        isInterrupted: false,
        interruptData: undefined,
        lastUpdated: new Date(),
      };

    default:
      return agentState;
  }
}

const AgentChatContext = createContext<ChatContextValue | null>(null);

export function AgentChatProvider({
  agentName,
  context,
  children,
}: {
  agentName: string;
  context?: AgentContext;
  children: React.ReactNode;
}) {
  const { track } = useAnalytics();
  const [state, dispatch] = useReducer(chatReducer, {
    ...initialAgentState,
    agentName,
    context,
  });

  // Create a tool call emitter instance for this agent
  const toolCallEmitterRef = React.useRef<ToolCallEmitter>(
    new ToolCallEmitter(),
  );

  // Prompt contexts state (separate from reducer for simpler management)
  const [promptContextsState, setPromptContextsState] = React.useState<
    PromptAttachment[]
  >([]);

  const getAgentState = useCallback((): AgentChatState => {
    return state || initialAgentState;
  }, [state]);

  const clearConversation = useCallback(() => {
    dispatch({ type: 'CLEAR_CONVERSATION' });
  }, []);

  const getLandingPageJson = useCallback(() => {
    return state?.landingPageJson || null;
  }, [state?.landingPageJson]);

  const setAgentContext = useCallback(
    <T extends keyof AgentContextMap>(context: AgentContextMap[T]) => {
      dispatch({
        type: 'SET_AGENT_CONTEXT',
        context,
      });
    },
    [],
  );

  const getAgentContext = useCallback((): AgentContext | null => {
    const context = state.context || null;
    console.log('AgentChatContext - getAgentContext called:', {
      agentName,
      context,
      agentState: state,
    });
    return context;
  }, [agentName, state]);

  // Thread management functions
  const getThreadId = useCallback((): string | null => {
    // First try to get agent-specific threadId
    const agentThreadId = state.currentThreadId;
    if (agentThreadId) {
      return agentThreadId;
    }

    return null;
  }, [state.currentThreadId]);

  const setThreadId = useCallback((threadId: string) => {
    console.log('🧵 Setting threadId for agent:', { threadId });
    dispatch({ type: 'SET_THREAD_ID', threadId });
  }, []);

  const generateThreadIdForAgent = useCallback((): string => {
    // Otherwise generate a new threadId
    const threadId = uuidv4();

    // Automatically set the generated threadId in the state
    setThreadId(threadId);

    return threadId;
  }, [setThreadId]);

  // Prompt contexts management
  const addPromptContext = useCallback((context: PromptAttachment) => {
    setPromptContextsState((prev) => [...prev, context]);
  }, []);

  const removePromptContext = useCallback((id: string) => {
    setPromptContextsState((prev) => prev.filter((ctx) => ctx.id !== id));
  }, []);

  const setPromptContexts = useCallback((contexts: PromptAttachment[]) => {
    setPromptContextsState(contexts);
  }, []);

  const updatePromptContext = useCallback(
    (id: string, updates: Partial<PromptAttachment>) => {
      setPromptContextsState((prev) =>
        prev.map((ctx) =>
          ctx.id === id ? ({ ...ctx, ...updates } as PromptAttachment) : ctx,
        ),
      );
    },
    [],
  );

  const clearPromptContexts = useCallback(() => {
    setPromptContextsState([]);
  }, []);

  // Helper function to emit tool calls
  const emitToolCall = useCallback((toolOutput: any, eventData: any) => {
    if (!toolOutput || !eventData?.toolName) return;

    // Parse the tool output - it comes as a ToolMessage with content as JSON string
    let parsedOutput;
    const input = eventData?.data?.input;

    try {
      if (typeof toolOutput.kwargs?.content === 'string') {
        parsedOutput = JSON.parse(toolOutput.kwargs.content);
      } else if (typeof toolOutput?.kwargs?.content === 'object') {
        parsedOutput = toolOutput.kwargs.content;
      } else if (typeof toolOutput === 'object') {
        parsedOutput = toolOutput;
      } else {
        console.log(
          '[AgentChatContext] No parseable content in tool output:',
          toolOutput,
        );
        return;
      }
    } catch (error) {
      console.error('[AgentChatContext] Failed to parse tool output:', error);
      return;
    }

    // Emit the tool call event
    const toolCallEvent = {
      toolName: eventData.toolName,
      input,
      output: parsedOutput,
      metadata: {
        toolCallId: eventData?.toolCallId || '',
        timestamp: Date.now(),
      },
    };

    console.log('[AgentChatContext] Emitting tool call:', toolCallEvent);
    toolCallEmitterRef.current.emit(toolCallEvent);
  }, []);

  // Internal methods for handling streaming (used by the chat component)
  const handleStreamEvent = useCallback(
    (event: StreamEvent) => {
      if (event.type === 'token') {
        // Use a ref or get fresh state via dispatch - for now let's update via dispatch
        // The reducer will handle checking if there's a streaming message to update
        dispatch({
          type: 'UPDATE_STREAMING_MESSAGE',
          content: event.data || '',
        });
      } else if (event.type === 'message_start') {
        // Start a new message - don't change working state as it's controlled by working events
        dispatch({
          type: 'START_MESSAGE',

          reason: event.data?.reason,
        });
      } else if (event.type === 'message_end') {
        // End the current streaming message
        dispatch({
          type: 'END_MESSAGE',
          reason: event.data?.reason,
        });
      } else if (event.type === 'tool_call_start') {
        // Handle tool call start - create a new tool call in the current/new message
        const toolCall: ToolCall = {
          id: uuidv4(), // Frontend generates unique ID for React keys
          toolCallId: event.data?.toolCallId, // Backend run_id - use this for matching
          toolName: event.data?.toolName || 'unknown',
          status: 'running',
          description: event.data?.description,
          startTime: new Date(),
          input: event.data?.data?.input, // Extract input from data.input
        };

        track('agent_tool_call_started', {
          agent_name: agentName,
          tool_name: toolCall.toolName,
          tool_call_id: toolCall.toolCallId,
        });

        dispatch({
          type: 'ADD_TOOL_CALL',

          toolCall,
        });
      } else if (event.type === 'tool_call_end') {
        // Complete the tool call using toolCallId (run_id) for precise matching
        const toolName = event.data?.toolName;
        const success = event.data?.data?.output?.success !== false;

        track('agent_tool_call_completed', {
          agent_name: agentName,
          tool_name: toolName,
          tool_call_id: event.data?.toolCallId,
          success: success,
        });

        dispatch({
          type: 'COMPLETE_TOOL_CALL',
          toolCallId: event.data?.toolCallId, // Use backend's run_id for matching
          toolName: toolName,
          input: event.data?.data?.input, // Extract input from data.input
          output: event.data?.data?.output, // Extract output from data.output
        });

        // Check if the tool output contains data to emit
        emitToolCall(event.data?.data?.output, event.data);
      } else if (event.type === 'working') {
        // Handle working state changes from backend
        const isWorking = event.data?.state === 'start';
        const status = isWorking
          ? event.data?.description ||
            `Working with ${event.data?.tool || 'tool'}...`
          : undefined;

        if (isWorking) {
          // Immediately update with backend working status (no delay for backend events)
          dispatch({
            type: 'SET_WORKING',

            working: true,
            status,
          });
        } else {
          // Immediately hide working indicator when done
          dispatch({
            type: 'SET_WORKING',

            working: false,
          });
        }
      } else if (event.type === 'error') {
        // Handle error events from SSE stream
        const errorMessage =
          event.data?.message || 'An error occurred during processing';

        track('agent_error_occurred', {
          agent_name: agentName,
          error_message: errorMessage,
          error_code: event.data?.code,
        });

        dispatch({ type: 'SET_ERROR', error: errorMessage });
        dispatch({ type: 'SET_STREAMING', streaming: false });
        dispatch({ type: 'SET_WORKING', working: false });

        // Also add an error message to the chat
        const errorChatMessage: ChatMessage = {
          id: uuidv4(),
          type: 'error',
          content: errorMessage,
          timestamp: new Date(),
          agentName,
          metadata: {
            details: event.data?.details,
            code: event.data?.code,
          },
        };
        dispatch({ type: 'ADD_MESSAGE', message: errorChatMessage });
      } else if (event.type === 'interrupt') {
        // Handle interrupt events - agent needs user input to continue
        const interruptData: InterruptData = {
          reason: event.data?.reason || 'human_input_required',
          message:
            event.data?.message || 'The agent needs your input to continue.',
          actions: event.data?.actions,
          allowFreeText: event.data?.allowFreeText,
          freeTextPlaceholder: event.data?.freeTextPlaceholder,
          context: event.data?.context,
        };

        track('agent_interrupted', {
          agent_name: agentName,
          interrupt_reason: interruptData.reason,
          has_actions: !!interruptData.actions?.length,
        });

        dispatch({ type: 'SET_INTERRUPTED', interruptData });
      } else if (event.type === 'resume') {
        // Handle resume events - agent is continuing from interrupt
        track('agent_resumed', {
          agent_name: agentName,
        });

        dispatch({ type: 'CLEAR_INTERRUPT' });
        dispatch({ type: 'SET_STREAMING', streaming: true });
      }
    },
    [agentName, emitToolCall, track],
  );

  const handleStreamComplete = useCallback(
    (_finalText: string, events: SSEGenericEvent[]) => {
      track('agent_stream_completed', {
        agent_name: agentName,
        events_count: events.length,
        final_text_length: _finalText.length,
      });

      dispatch({ type: 'FINISH_STREAMING', events });
    },
    [agentName, track],
  );

  const handleStreamError = useCallback(
    (error: Error) => {
      track('agent_stream_error', {
        agent_name: agentName,
        error_message: error.message,
      });

      dispatch({ type: 'SET_ERROR', error: error.message });
      dispatch({ type: 'SET_STREAMING', streaming: false });
      dispatch({ type: 'SET_WORKING', working: false });

      // Add an error message to the chat
      const errorChatMessage: ChatMessage = {
        id: uuidv4(),
        type: 'error',
        content: error.message,
        timestamp: new Date(),
        agentName,
        metadata: {
          details: error.stack,
        },
      };
      dispatch({ type: 'ADD_MESSAGE', message: errorChatMessage });
    },
    [agentName, track],
  );

  const setStreamingWithIds = useCallback((runId: string, threadId: string) => {
    dispatch({
      type: 'SET_STREAMING',
      streaming: true,
      runId,
      threadId,
    });
  }, []);

  const agentSSE = useAgentSSE(agentName, {
    onStreamEvent: (event) => handleStreamEvent(event),
    onStreamComplete: (finalText, events) =>
      handleStreamComplete(finalText, events),
    onError: (error) => handleStreamError(error),
  });

  const stopStreaming = useCallback(async () => {
    // Stop all running tool calls
    dispatch({ type: 'STOP_ALL_TOOL_CALLS' });
    dispatch({ type: 'SET_STREAMING', streaming: false });
    dispatch({ type: 'SET_WORKING', working: false });
    await agentSSE.stop();
  }, [agentSSE]);

  const resetAgentSession = useCallback(async () => {
    // First stop any running agent
    await stopStreaming();

    // Clear the conversation
    dispatch({ type: 'CLEAR_CONVERSATION' });

    // Generate a new thread ID
    const newThreadId = uuidv4();
    dispatch({ type: 'SET_THREAD_ID', threadId: newThreadId });

    console.log('🔄 Agent session reset with new threadId:', newThreadId);
  }, [stopStreaming]);

  const sendMessage = useCallback(
    async (message: string, attachments?: AgentRunAttachment[]) => {
      const messageId = uuidv4();

      // Clear any current interrupt state and last error before sending new message
      dispatch({ type: 'CLEAR_INTERRUPT' });
      dispatch({ type: 'REMOVE_LAST_ERROR' });

      // Add user message
      const userMessage: ChatMessage = {
        id: messageId,
        type: 'user',
        content: message,
        timestamp: new Date(),
        ...(attachments && attachments.length > 0 && { attachments }),
      };

      dispatch({ type: 'ADD_MESSAGE', message: userMessage });
      dispatch({ type: 'SET_LOADING', loading: true });
      dispatch({ type: 'SET_ERROR', error: null });

      // Track message sent
      const isFirstMessage = state.messages.length === 0;
      track(
        isFirstMessage ? 'agent_conversation_started' : 'agent_message_sent',
        {
          agent_name: agentName,
          message_length: message.length,
          has_attachments: !!attachments?.length,
          attachments_count: attachments?.length || 0,
          message_number:
            state.messages.filter((m) => m.type === 'user').length + 1,
          thread_id: getThreadId() || 'new',
        },
      );

      // Immediately show working indicator when user sends message
      dispatch({
        type: 'SET_WORKING',
        working: true,
        status: 'Working',
      });

      // Don't create a placeholder message anymore - wait for message_start event from backend
      dispatch({ type: 'SET_LOADING', loading: false });
      dispatch({ type: 'SET_STREAMING', streaming: true });

      // Prepare context for backend
      const runContextPayload: any = { ...getAgentContext() };

      if (attachments && attachments.length > 0) {
        console.log('AgentChat - Received attachments:', attachments);

        console.log('AgentChat - Final payload for', agentName, ':', {
          runContext: runContextPayload,
          attachments,
        });
      }

      // Get or generate thread ID using centralized management
      let threadId = getThreadId();
      if (!threadId) {
        console.log('🧵 No existing threadId, generating new one');
        threadId = generateThreadIdForAgent();
      } else {
        console.log('🧵 Using existing threadId:', threadId);
      }

      try {
        // Start the SSE stream with enhanced context and attachments
        const runId = await agentSSE.start(
          message,
          threadId,
          runContextPayload,
          attachments || [],
        );

        track('agent_stream_started', {
          agent_name: agentName,
          run_id: runId,
          thread_id: threadId,
          has_context:
            !!runContextPayload && Object.keys(runContextPayload).length > 0,
        });

        // Update the streaming state with both runId and threadId
        setStreamingWithIds(runId, threadId);
      } catch (_error) {
        // Error is already handled by the useAgentSSE hook's onError callback
        // Just ensure streaming state is cleaned up
        dispatch({ type: 'SET_STREAMING', streaming: false });
        dispatch({ type: 'SET_WORKING', working: false });
      }
    },
    [
      agentName,
      agentSSE,
      generateThreadIdForAgent,
      getAgentContext,
      getThreadId,
      setStreamingWithIds,
      state.messages,
      track,
    ],
  );

  // Tool call subscription method
  const subscribeToToolCall = useCallback(
    (toolName: string, handler: ToolCallHandler): (() => void) => {
      return toolCallEmitterRef.current.on(toolName, handler);
    },
    [],
  );

  // Resume from an interrupt with user's response
  const resumeFromInterrupt = useCallback(
    async (response: InterruptResponse) => {
      const runId = state.currentRunId;
      if (!runId) {
        console.error('Cannot resume: no current run ID');
        dispatch({
          type: 'SET_ERROR',
          error: 'Cannot resume: no active run found',
        });
        return;
      }

      // Clear the interrupt state optimistically
      dispatch({ type: 'CLEAR_INTERRUPT' });
      dispatch({ type: 'SET_STREAMING', streaming: true });
      dispatch({ type: 'SET_WORKING', working: true, status: 'Resuming...' });

      // Track the resume attempt
      track('agent_resume_attempted', {
        agent_name: agentName,
        run_id: runId,
        has_selected_action: !!response.selectedActionId,
        has_free_text: !!response.freeTextResponse,
      });

      try {
        const success = await agentSSE.resume(runId, response);
        if (!success) {
          dispatch({ type: 'SET_ERROR', error: 'Failed to resume agent run' });
          dispatch({ type: 'SET_STREAMING', streaming: false });
          dispatch({ type: 'SET_WORKING', working: false });
        }
      } catch (error) {
        console.error('Error resuming agent:', error);
        dispatch({
          type: 'SET_ERROR',
          error:
            error instanceof Error
              ? error.message
              : 'Unknown error resuming agent',
        });
        dispatch({ type: 'SET_STREAMING', streaming: false });
        dispatch({ type: 'SET_WORKING', working: false });
      }
    },
    [agentName, agentSSE, state.currentRunId, track],
  );

  const contextValue: ChatContextValue = {
    getAgentState,
    sendMessage,
    clearConversation,
    stopStreaming,
    setAgentContext,
    getAgentContext,
    getThreadId,
    setThreadId,
    generateThreadIdForAgent,
    getLandingPageJson,
    resetAgentSession,
    promptContexts: promptContextsState,
    addPromptContext,
    removePromptContext,
    setPromptContexts,
    updatePromptContext,
    clearPromptContexts,
    subscribeToToolCall,
    resumeFromInterrupt,
  };

  return (
    <AgentChatContext.Provider value={contextValue}>
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
}
